/** Incident and maintenance tickets (backlog #21): types and pure rules shared by API and web. */
import { issueDueComment } from './issueDue';

export type IncidentType = 'maintenance' | 'accident' | 'near_miss' | 'other';
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';
export type IncidentStatus = 'new' | 'acknowledged' | 'in_progress' | 'on_hold' | 'resolved' | 'closed';
export const INCIDENT_TYPES: { key: IncidentType; label: string }[] = [
  { key: 'maintenance', label: 'Maintenance / repair' }, { key: 'accident', label: 'Accident (injury)' },
  { key: 'near_miss', label: 'Near miss' }, { key: 'other', label: 'Other issue' },
];
export const INCIDENT_SEVERITIES: { key: IncidentSeverity; label: string }[] = [
  { key: 'low', label: 'Low' }, { key: 'medium', label: 'Medium' }, { key: 'high', label: 'High' }, { key: 'critical', label: 'Critical' },
];
export const INCIDENT_STATUSES: { key: IncidentStatus; label: string; open: boolean }[] = [
  { key: 'new', label: 'New', open: true }, { key: 'acknowledged', label: 'Acknowledged', open: true }, { key: 'in_progress', label: 'In progress', open: true },
  { key: 'on_hold', label: 'On hold', open: true }, { key: 'resolved', label: 'Resolved', open: false }, { key: 'closed', label: 'Closed', open: false },
];
export const isHsType = (t: IncidentType) => t === 'accident' || t === 'near_miss';

/** Default SLA targets (minutes) when neither the category nor the workspace sets one. */
export const DEFAULT_SLA: Record<IncidentSeverity, { response: number; resolve: number }> = {
  critical: { response: 30, resolve: 4 * 60 }, high: { response: 2 * 60, resolve: 24 * 60 },
  medium: { response: 8 * 60, resolve: 3 * 24 * 60 }, low: { response: 24 * 60, resolve: 7 * 24 * 60 },
};

export type PersonRole = 'employee' | 'agency' | 'contractor' | 'visitor' | 'member_of_public';
export type SpecifiedInjury = 'fracture' | 'amputation' | 'sight_loss' | 'crush_head_torso' | 'serious_burn' | 'scalping' | 'unconscious' | 'asphyxia';
export const SPECIFIED_INJURIES: { key: SpecifiedInjury; label: string }[] = [
  { key: 'fracture', label: 'Fracture (other than fingers, thumbs or toes)' }, { key: 'amputation', label: 'Amputation' },
  { key: 'sight_loss', label: 'Permanent loss or reduction of sight' }, { key: 'crush_head_torso', label: 'Crush injury to the head or torso' },
  { key: 'serious_burn', label: 'Serious burn (over 10% of the body, or damaging eyes, breathing or vital organs)' },
  { key: 'scalping', label: 'Scalping needing hospital treatment' }, { key: 'unconscious', label: 'Loss of consciousness from head injury or asphyxia' },
  { key: 'asphyxia', label: 'Injury from working in an enclosed space' },
];

/** Accident-book fields (what a UK accident book / F2508 typically asks for). All optional. */
export interface HsDetails {
  occurred_at?: string | null;
  person_name?: string | null;
  person_role?: PersonRole | null;
  person_contact?: string | null;
  injury_description?: string | null;
  body_part?: string | null;
  specified_injuries?: SpecifiedInjury[];
  first_aid_given?: boolean | null;
  first_aider?: string | null;
  taken_to_hospital?: boolean | null;
  fatal?: boolean | null;
  /** Expected to be off work (or unable to do normal duties) for more than seven consecutive days, not counting the day of the accident. */
  over_seven_days?: boolean | null;
  dangerous_occurrence?: boolean | null;
  witnesses?: string | null;
  immediate_action?: string | null;
  accident_book_ref?: string | null;
}

/**
 * "Possible RIDDOR": a prompt for a manager to check whether the event must be reported to the HSE. It is a
 * heuristic over the common triggers, not legal advice, and a manager can always set or clear the flag.
 */
export function riddorReasons(type: IncidentType, hs: HsDetails | null | undefined): string[] {
  if (!hs || !isHsType(type)) return [];
  const out: string[] = [];
  const worker = !hs.person_role || hs.person_role === 'employee' || hs.person_role === 'agency' || hs.person_role === 'contractor';
  if (hs.fatal) out.push('Death');
  if (worker && hs.specified_injuries?.length) out.push('Specified injury to a worker');
  if (worker && hs.over_seven_days) out.push('Worker off for more than 7 days');
  if (!worker && hs.taken_to_hospital) out.push('Non-worker taken to hospital for treatment');
  if (hs.dangerous_occurrence) out.push('Possible dangerous occurrence');
  return out;
}

export type SlaState = 'ok' | 'due_soon' | 'breached' | 'met' | 'none';
/** Resolution SLA state for a ticket at `now`. "Due soon" is the last 20% of the window (at least 30 minutes). */
export function slaState(t: { status: IncidentStatus; created_at: string; resolve_due_at: string | null; resolved_at: string | null }, now = Date.now()): SlaState {
  if (!t.resolve_due_at) return 'none';
  const due = Date.parse(t.resolve_due_at);
  if (t.status === 'resolved' || t.status === 'closed') return t.resolved_at && Date.parse(t.resolved_at) <= due ? 'met' : 'breached';
  if (now > due) return 'breached';
  const window = due - Date.parse(t.created_at);
  return due - now <= Math.max(window * 0.2, 30 * 60_000) ? 'due_soon' : 'ok';
}

export interface IncidentCategory { id: string; name: string; kind: 'maintenance' | 'hs' | 'other'; default_team_id: string | null; default_assignee_id: string | null; sort_order: number; archived_at: string | null;
  /** t629u: the subjects this category is for (empty = all). */
  applies_to?: import('./issueSubjects').IssueSubject[] }
export interface IncidentSla { id: string; category_id: string | null; severity: IncidentSeverity; response_minutes: number; resolve_minutes: number }
export interface Incident {
  id: string; ref: number; type: IncidentType; category_id: string | null; category_name: string | null; location_id: string; location_name: string;
  location_detail: string | null; title: string; description: string | null; severity: IncidentSeverity; status: IncidentStatus;
  reporter_id: string; reporter_name: string;
  /** The first assignee (kept for older app builds); `assignees` is everyone, in the order they were added (t699u). */
  assignee_id: string | null; assignee_name: string | null;
  assignee_ids?: string[]; assignees?: IssueAssignee[];
  team_id: string | null; team_name: string | null;
  photo_ids: string[]; possible_riddor: boolean; source: 'user' | 'checklist' | 'fleet'; source_id: string | null;
  /** t629u: what it is about. Building issues have neither link; equipment is at the building; a vehicle's home building is the building. */
  subject_type?: import('./issueSubjects').IssueSubject; equipment_id?: string | null; equipment_name?: string | null; equipment_identifier?: string | null;
  equipment_status?: 'in_use' | 'out_of_use' | 'retired' | null; equipment_category_id?: string | null;
  vehicle_id?: string | null; vehicle_reg?: string | null; vehicle_name?: string | null; vehicle_status?: 'active' | 'off_road' | 'retired' | null;
  response_due_at: string | null; resolve_due_at: string | null; first_response_at: string | null; resolved_at: string | null; closed_at: string | null;
  created_at: string; updated_at: string; comment_count: number; following: boolean;
  /** t276u: new to you - never opened, or someone else changed or commented on it since you last opened it. */
  unread?: boolean;
  /** t127: newest comment or change in the issue's thread (null if nothing since it was reported). */
  latest_update?: { at: string; kind: 'comment' | 'change'; text: string; actor_id: string | null; actor_name: string | null } | null;
  /** #36: the report-form version this was submitted with, its sections and the answers. */
  form_id: string | null; form_version: number | null; form_name: string | null; form_version_title: string | null;
  form_sections: import('../scoped-forms').FormSection[] | null; answers: import('../forms').FormValues | null;
}
/** t699u: one person an issue is assigned to. */
export interface IssueAssignee { id: string; name: string }
export interface IncidentComment { id: string; author_id: string; author_name: string; body: string; photo_ids: string[]; created_at: string }
export interface IncidentEvent { id: number; actor_id: string | null; actor_name: string | null; kind: string; from_value: string | null; to_value: string | null; created_at: string }
export interface IncidentDetail extends Incident {
  /** Present only for holders of ops.incident.hs_view at the building (and the reporter). */
  hs: HsDetails | null; riddor_reasons: string[]; hs_redacted: boolean;
  comments: IncidentComment[]; events: IncidentEvent[]; followers: { user_id: string; display_name: string }[];
  can_manage: boolean;
  /** t699u: managers, and anyone the issue is assigned to (they can move its status on). */
  can_update_status?: boolean;
}
export interface IncidentInput {
  type: IncidentType; category_id?: string | null; location_id: string; location_detail?: string | null; title: string; description?: string | null;
  severity?: IncidentSeverity; photo_ids?: string[]; hs?: HsDetails | null;
  /** t335u item 4: a defect on this piece of equipment (same building). */
  equipment_id?: string | null;
  /** t629u: take the equipment out of use as well (people who manage equipment at the building). */
  take_out_of_use?: boolean;
  /** #36: the report form to use (default: the most specific one) and its answers. */
  form_id?: string | null; answers?: Record<string, unknown> | null;
}
export interface IncidentReportRow { key: string; label: string; open: number; resolved: number; breached: number; avg_resolve_hours: number | null; riddor: number }

export interface IncidentEventNames {
  assignee?: string | null; team?: string | null; category?: string | null;
  /** t699u: names by user id, for assignee changes (`from` / `to` are comma-separated ids), and who made the change. */
  people?: Record<string, string>; actor_id?: string | null;
  /** t698u: the actor assigned it to themselves (single-assignee callers; `actor_id` does the same for lists). */
  self?: boolean;
}

/** t699u: an issue's assignees, everyone on it, in order (the column alone for older rows). */
export function issueAssigneesOf(i: Pick<Incident, 'assignee_id' | 'assignee_name'> & { assignees?: IssueAssignee[] | null }): IssueAssignee[] {
  if (i.assignees) return i.assignees;
  return i.assignee_id ? [{ id: i.assignee_id, name: i.assignee_name ?? 'Someone' }] : [];
}
/** "Sam", "Sam and Jo", "Sam, Jo and Alex". */
export function listNames(names: readonly string[]): string {
  return names.length <= 1 ? names[0] ?? '' : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
/** History rows keep the assignees as comma-separated ids (older rows: one id). */
export function assigneeIdsOf(v: string | null | undefined): string[] {
  return (v ?? '').split(',').map(x => x.trim()).filter(Boolean);
}

/**
 * t699u: the wording of an assignee change, after the actor's name: "assigned this to Sam and Jo", "assigned this to
 * themselves" (the actor took it on alone), "removed Sam from this issue", "unassigned this issue". Only the people
 * added are named; when nobody was added, the people taken off are.
 */
export function issueAssigneeChangeText(from: string | null, to: string | null, names: IncidentEventNames = {}): string {
  const before = assigneeIdsOf(from); const after = assigneeIdsOf(to);
  if (!after.length) return 'unassigned this issue';
  const self = (id: string) => (names.actor_id ? id === names.actor_id : Boolean(names.self) && after.length === 1);
  const name = (id: string) => (self(id) ? 'themselves' : names.people?.[id] ?? (after.length === 1 ? names.assignee : null) ?? 'someone');
  const added = after.filter(id => !before.includes(id));
  if (added.length) {
    // The actor comes first when they took it on with others ("themselves and Sam").
    const ordered = [...added.filter(self), ...added.filter(id => !self(id))];
    return `assigned this to ${listNames(ordered.map(name))}`;
  }
  const removed = before.filter(id => !after.includes(id));
  if (removed.length) return `removed ${listNames(removed.map(id => (names.actor_id && id === names.actor_id ? 'themselves' : names.people?.[id] ?? 'someone')))} from this issue`;
  return `assigned this to ${listNames(after.map(name))}`;
}

/**
 * Issue threads (#45): the wording of a change posted as a system line ("Sarah changed the status from New to
 * In progress"; the actor's name is shown separately). Same wording as workforce.incident_event_text() in SQL.
 */
export function incidentEventText(kind: string, from: string | null, to: string | null, names: IncidentEventNames = {}): string {
  const st = (v: string | null) => INCIDENT_STATUSES.find(s => s.key === v)?.label ?? v ?? '?';
  const sev = (v: string | null) => INCIDENT_SEVERITIES.find(s => s.key === v)?.label ?? v ?? '?';
  switch (kind) {
    case 'created': return 'reported this issue';
    case 'status': return `changed the status from ${st(from)} to ${st(to)}`;
    case 'severity': return `changed the severity from ${sev(from)} to ${sev(to)}`;
    case 'assignee': return issueAssigneeChangeText(from, to, names);
    case 'team': return to ? `assigned this to the ${names.team ?? 'unknown'} team` : 'removed the team';
    case 'category': return to ? `changed the category to ${names.category ?? 'unknown'}` : 'removed the category';
    case 'riddor_flag': return to === 'true' ? 'marked this as possible RIDDOR' : 'cleared the possible RIDDOR flag';
    case 'due_date': return issueDueComment(from, to) || 'changed the due date'; // t565u
    case 'edited': { // t246u: newer rows list the fields ("title,description")
      const f = (to ?? '').split(',').map(x => ({ title: 'title', description: 'description', location_detail: 'location' } as Record<string, string>)[x]).filter(Boolean);
      return f.length ? `edited the ${f.length > 1 ? `${f.slice(0, -1).join(', ')} and ${f[f.length - 1]}` : f[0]}` : 'edited the details';
    }
    default: return kind.replace(/_/g, ' ');
  }
}
